import React, { useState, useMemo } from 'react';
import { Mafs, Coordinates, Line, Text, Polygon, MovablePoint, Point, Theme } from 'mafs';

const formatNum = (val) => {
    if (val === null || val === undefined || isNaN(val)) return '0';
    return Number.isInteger(val) ? String(val) : val.toFixed(2);
};

export const DistanceFormula = ({ config }) => {
    const [pointA, setPointA] = useState({ x: config.point1?.x || 2, y: config.point1?.y || 3 });
    const [pointB, setPointB] = useState({ x: config.point2?.x || 8, y: config.point2?.y || 7 });

    const [showTriangle, setShowTriangle] = useState(config.showTriangle !== false);
    const [showMidpoint, setShowMidpoint] = useState(config.showMidpoint !== false);

    const deltaX = useMemo(() => pointB.x - pointA.x, [pointA, pointB]);
    const deltaY = useMemo(() => pointB.y - pointA.y, [pointA, pointB]);
    const distance = useMemo(() => Math.sqrt(deltaX * deltaX + deltaY * deltaY), [deltaX, deltaY]);

    const midpoint = useMemo(() => ({ x: (pointA.x + pointB.x) / 2, y: (pointA.y + pointB.y) / 2 }), [pointA, pointB]);
    const gradient = useMemo(() => deltaX === 0 ? null : deltaY / deltaX, [deltaX, deltaY]);

    const renderGraph = () => {
        return (
            <div style={{ marginTop: '20px' }}>
                <Mafs viewBox={{ x: [-2, 12], y: [-2, 12] }} height={400} pan={false}>
                    <Coordinates.Cartesian />

                    {showTriangle && (
                        <>
                            <Line.Segment point1={[pointA.x, pointA.y]} point2={[pointB.x, pointA.y]} color={Theme.green} style="dashed" weight={2} />
                            <Text x={(pointA.x + pointB.x) / 2} y={pointA.y - 0.5}>Δx = {formatNum(Math.abs(deltaX))}</Text>

                            <Line.Segment point1={[pointB.x, pointA.y]} point2={[pointB.x, pointB.y]} color={Theme.green} style="dashed" weight={2} />
                            <Text x={pointB.x + 0.8} y={(pointA.y + pointB.y) / 2}>Δy = {formatNum(Math.abs(deltaY))}</Text>

                            <Polygon points={[[pointB.x - 0.4, pointA.y], [pointB.x - 0.4, pointA.y + 0.4], [pointB.x, pointA.y + 0.4]]} color={Theme.green} fillOpacity={0} />
                        </>
                    )}

                    <Line.Segment point1={[pointA.x, pointA.y]} point2={[pointB.x, pointB.y]} color={Theme.blue} weight={3} />

                    <Text x={(pointA.x + pointB.x) / 2 - 1.5} y={(pointA.y + pointB.y) / 2 + 0.8}>d = {formatNum(distance)}</Text>

                    <MovablePoint point={[pointA.x, pointA.y]} onMove={p => setPointA({ x: p[0], y: p[1] })} color={Theme.red} />
                    <Text x={pointA.x - 0.5} y={pointA.y - 0.8}>A({formatNum(pointA.x)}, {formatNum(pointA.y)})</Text>

                    <MovablePoint point={[pointB.x, pointB.y]} onMove={p => setPointB({ x: p[0], y: p[1] })} color={Theme.red} />
                    <Text x={pointB.x + 0.5} y={pointB.y + 0.8}>B({formatNum(pointB.x)}, {formatNum(pointB.y)})</Text>

                    {showMidpoint && (
                        <>
                            <Point x={midpoint.x} y={midpoint.y} color={Theme.indigo} />
                            <Text x={midpoint.x + 0.5} y={midpoint.y - 0.5}>M({formatNum(midpoint.x)}, {formatNum(midpoint.y)})</Text>
                        </>
                    )}
                </Mafs>
            </div>
        );
    };

    return (
        <div className="ch-distance-formula">
            <div style={{ padding: '15px', background: '#f0f5ff', borderRadius: '8px', marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: 600 }}>Distance Formula</h3>
                <div style={{ fontFamily: 'monospace', fontSize: '15px', color: '#1890ff' }}>
                    d = √[(x₂ - x₁)² + (y₂ - y₁)²]
                </div>
            </div>

            <div className="ch-controls" style={{ display: 'flex', gap: '15px', padding: '15px', background: '#fafafa', borderRadius: '8px', marginBottom: '20px' }}>
                <label style={{ cursor: 'pointer', fontWeight: 500 }}>
                    <input type="checkbox" checked={showTriangle} onChange={e => setShowTriangle(e.target.checked)} style={{ marginRight: '5px' }} />
                    Show Right Triangle
                </label>
                <label style={{ cursor: 'pointer', fontWeight: 500 }}>
                    <input type="checkbox" checked={showMidpoint} onChange={e => setShowMidpoint(e.target.checked)} style={{ marginRight: '5px' }} />
                    Show Midpoint
                </label>
            </div>

            <div style={{ padding: '10px', background: '#e6f7ff', borderRadius: '8px', marginBottom: '20px' }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#0050b3' }}>
                    💡 <strong>Tip:</strong> Drag points A and B to explore different distances and midpoints
                </p>
            </div>

            {renderGraph()}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
                <div style={{ padding: '20px', background: '#fff7e6', border: '2px solid #ffc53d', borderRadius: '8px' }}>
                    <h4 style={{ marginTop: 0, color: '#d48806' }}>📏 Distance Calculation</h4>
                    <div style={{ lineHeight: '1.8' }}>
                        <p style={{ margin: '5px 0', fontFamily: 'monospace', fontSize: '14px' }}>
                            d = √[(x₂ - x₁)² + (y₂ - y₁)²]
                        </p>
                        <p style={{ margin: '5px 0', fontFamily: 'monospace', fontSize: '14px' }}>
                            d = √[({formatNum(pointB.x)} - {formatNum(pointA.x)})² + ({formatNum(pointB.y)} - {formatNum(pointA.y)})²]
                        </p>
                        <p style={{ margin: '5px 0', fontFamily: 'monospace', fontSize: '14px' }}>
                            d = √[({formatNum(deltaX)})² + ({formatNum(deltaY)})²]
                        </p>
                        <p style={{ margin: '5px 0', fontFamily: 'monospace', fontSize: '14px' }}>
                            d = √[{formatNum(deltaX * deltaX)} + {formatNum(deltaY * deltaY)}]
                        </p>
                        <p style={{ margin: '5px 0', fontFamily: 'monospace', fontSize: '14px' }}>
                            d = √{formatNum(deltaX * deltaX + deltaY * deltaY)}
                        </p>
                        <p style={{ margin: '10px 0 5px 0', fontFamily: 'monospace', fontSize: '16px', fontWeight: 'bold', color: '#d48806' }}>
                            d = {formatNum(distance)} {config.units || 'units'}
                        </p>
                    </div>
                </div>

                <div style={{ padding: '20px', background: '#f9f0ff', border: '2px solid #d3adf7', borderRadius: '8px' }}>
                    <h4 style={{ marginTop: 0, color: '#531dab' }}>📐 Properties</h4>
                    <div style={{ lineHeight: '1.8' }}>
                        <p style={{ margin: '5px 0', fontFamily: 'monospace', fontSize: '14px' }}>
                            <strong>Point A:</strong> ({formatNum(pointA.x)}, {formatNum(pointA.y)})
                        </p>
                        <p style={{ margin: '5px 0', fontFamily: 'monospace', fontSize: '14px' }}>
                            <strong>Point B:</strong> ({formatNum(pointB.x)}, {formatNum(pointB.y)})
                        </p>
                        <p style={{ margin: '10px 0 5px 0', paddingTop: '10px', borderTop: '2px solid #efdbff', fontFamily: 'monospace', fontSize: '14px' }}>
                            <strong>Midpoint M:</strong> ({formatNum(midpoint.x)}, {formatNum(midpoint.y)})
                        </p>
                        <p style={{ margin: '5px 0', fontFamily: 'monospace', fontSize: '14px' }}>
                            <strong>Gradient (m):</strong> {gradient === null ? 'Undefined' : formatNum(gradient)}
                        </p>
                        <p style={{ margin: '5px 0', fontSize: '12px', color: '#666' }}>
                            {gradient === null ? '(Vertical line)' : gradient === 0 ? '(Horizontal line)' : ''}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};